import { Grid, GridRow } from '../Grid';
import { Title, Text as Paragraph } from '../Typography';
import { SearchResultWrapper, Image, SubHeadingText, ImageStyle } from './styles';
import FadeInSection from '../FadeInSection';

import type { ImageType } from '../../types/ICardData';
import NextImage from 'components/NextImage';
import { StringService } from 'service/string/stringService';
import { useGlobalContext } from 'components/Context/hooks/useGlobalContext';
import type { PathSegment } from 'service/localization/getPathTranslations';
import { NextLink } from 'components/NextLink';
import { useHits } from 'react-instantsearch';

export const OBJECT_TYPE = {
  TIPS_TRICKS: 'TIPS_TRICKS',
  PRODUCTS: 'PRODUCTS',
  RECIPES: 'RECIPES',
  ARTICLES: 'ARTICLES',
};

export const Hit = ({
  hit,
  copy = null,
  searchValue: searchValueFromGlobalSearch,
  onClick,
}: {
  hit: {
    id: string;
    objectType: string;
    slug: string;
    title: string;
    images: ImageType[];
    type: string;
    category: string;
  };
  copy?: {
    searchCategoryTips: string;
    searchCategoryProducts: string;
    searchCategoryRecipes: string;
    searchCategoryArticles: string;
  } | null;
  searchValue?: string;
  onClick?: () => void;
}) => {
  const { results } = useHits();
  const searchValue = results?.query ?? searchValueFromGlobalSearch ?? '';

  const { pathTranslations } = useGlobalContext();
  const finalImageList = {
    desktop: '',
    mobile: '',
    tablet: '',
    hero: '',
  };

  hit?.images?.forEach((imageDetail: ImageType) => {
    if (imageDetail?.url) {
      if (imageDetail?.width === 964) {
        finalImageList.desktop = imageDetail?.url;
      } else if (imageDetail?.width === 636) {
        finalImageList.tablet = imageDetail?.url;
      } else if (imageDetail?.width === 302 || imageDetail?.width === 320) {
        finalImageList.mobile = imageDetail?.url;
      } else if (imageDetail?.width === 1272 || imageDetail?.width === 1440) {
        finalImageList.hero = imageDetail?.url;
      } else {
        finalImageList.hero = imageDetail?.url;
      }
    } else if (typeof imageDetail?.Url === 'string' && imageDetail.Url) {
      if (imageDetail?.Width === 964) {
        finalImageList.desktop = imageDetail?.Url;
      } else if (imageDetail?.Width === 636) {
        finalImageList.tablet = imageDetail?.Url;
      } else if (imageDetail?.Width === 302) {
        finalImageList.mobile = imageDetail?.Url;
      } else if (imageDetail?.Width === 1272) {
        finalImageList.hero = imageDetail?.Url;
      } else {
        finalImageList.hero = imageDetail?.Url;
      }
    }
  });

  const pageUrl = () => {
    if (hit.objectType === 'TIPS_TRICKS') {
      return `${pathTranslations?.tipAndTrick}${hit.slug || hit.id}`;
    }
    if (hit.objectType === 'PRODUCTS') {
      return `${pathTranslations?.product}${hit.slug || hit.id}`;
    }
    if (hit.objectType === 'RECIPES') {
      return `${pathTranslations?.recipe}${hit.slug || hit.id}`;
    }
    if (hit.objectType === 'ARTICLES') {
      if (hit.type) {
        if (hit?.type === 'generic') {
          return `${pathTranslations?.genericPage}${hit.slug || hit.id}`;
        }
        return `${
          pathTranslations?.[StringService.lowerCaseFirstLetter(hit.type) as PathSegment]
        }${hit.slug || hit.id}`;
      }
      return `${pathTranslations?.genericPage}${hit.slug || hit.id}`;
    }
    return undefined;
  };

  const dictionaryCategory = (hits: { objectType: string }) => {
    if (hits.objectType === 'TIPS_TRICKS') {
      return copy?.searchCategoryTips || 'Tips & Tricks';
    }

    if (hits.objectType === 'PRODUCTS') {
      return copy?.searchCategoryProducts || 'Products';
    }
    if (hits.objectType === 'RECIPES') {
      return copy?.searchCategoryRecipes || 'Recipes';
    }
    if (hits.objectType === 'ARTICLES') {
      return copy?.searchCategoryArticles || 'Articles';
    }
    return false;
  };

  const searchTerm = searchValue;
  const searchTermRegex = new RegExp(
    // Escape special regex characters in the search term,
    // otherwise you will get an invalid regex
    `(${searchTerm.replace(/[.\\+*?[^\]$(){}=!<>|:-]/g, '\\$&')})`,
    'gi',
  );

  const getBoldFilteredText = (text: string, term: string) => {
    const substrings = text?.split(searchTermRegex);
    return (
      <>
        {substrings.map((substring, index) =>
          substring.toLowerCase() === term.toLowerCase() ? (
            <strong key={index}>{substring}</strong>
          ) : (
            substring
          ),
        )}
      </>
    );
  };

  return (
    <SearchResultWrapper data-test="search-results" className="search-results" key={hit?.id}>
      <FadeInSection delay="150ms" fadeIn>
        {hit && (
          <div className="search-result-list-js">
            <Grid>
              <GridRow>
                <NextLink
                  className="flex"
                  href={pageUrl() ?? ''}
                  onClick={onClick}
                  data-test="result-row"
                >
                  <Image className={ImageStyle}>
                    <NextImage
                      src={
                        finalImageList?.mobile ||
                        finalImageList?.tablet ||
                        finalImageList?.desktop ||
                        finalImageList?.hero
                      }
                      alt={hit?.title}
                      objectFit="contain"
                    />
                  </Image>
                  <div>
                    <Paragraph tag="p" type="leadxs" className="mb-2.5 text-left">
                      <SubHeadingText>
                        {dictionaryCategory(hit)} {hit?.category && `/ ${hit?.category}`}
                      </SubHeadingText>
                    </Paragraph>
                    <Title tag="h2" type="xs" className="font-normal product-title text-left">
                      {getBoldFilteredText(hit?.title || '', searchValue)}
                    </Title>
                  </div>
                </NextLink>
              </GridRow>
            </Grid>
          </div>
        )}
      </FadeInSection>
    </SearchResultWrapper>
  );
};
